/*
  Personalization (custom engraving) for Impulse.

  1. <product-customization>: the "Customize it" toggle on the product page.
     The text field is submitted as a line item property on the product form.

  2. Fee handling: when a personalized item is added and a fee product is set
     (Theme settings > Personalization), the fee product is added in the same
     /cart/add.js request, linked to the item via a hidden `_for_variant`
     property. The theme's own add-to-cart handler is bypassed for that one
     submit and its `ajaxProduct:added` event is re-dispatched so the cart
     drawer opens as usual.

  3. Sync: whenever the cart changes (`cart:updated`, page load) the fee
     quantity is reconciled with the personalized items — removed when the
     item is removed, matched when the quantity changes, re-added if missing.

  Config comes from `theme.customization` (layout/theme.liquid).
*/
(function () {
  'use strict';

  var cfg = (window.theme && window.theme.customization) || {};
  var PROPERTY = cfg.propertyName || 'Engraving';
  var FEE_ID = cfg.feeVariantId ? Number(cfg.feeVariantId) : null;
  var FEE_ENABLED = !!(FEE_ID && cfg.feeAvailable);
  var LINK_PROPERTY = '_for_variant';
  var MARK_PROPERTY = '_personalized';
  var FOR_PROPERTY = 'For';

  function routes() {
    var r = (window.theme && window.theme.routes) || {};
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return {
      cart: r.cart || root + 'cart.js',
      cartAdd: r.cartAdd || root + 'cart/add.js',
      cartChange: r.cartChange || root + 'cart/change.js',
      cartPage: r.cartPage || root + 'cart'
    };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (res) { return res.json(); });
  }

  function getCart() {
    return fetch(routes().cart + '?t=' + Date.now(), { credentials: 'same-origin' })
      .then(function (res) { return res.json(); });
  }

  function hasText(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  /* ------------------------------------------------------------------
     Product page component
  ------------------------------------------------------------------ */
  var MAX_FILE_BYTES = 20 * 1024 * 1024; // Shopify's limit for uploaded line item files

  class ProductCustomization extends HTMLElement {
    constructor() {
      super();
      this.toggle = this.querySelector('[data-personalization-toggle]');
      this.panel = this.querySelector('[data-personalization-panel]');
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-personalization-input]'));
      this.input = this.inputs[0] || null;
      this.file = this.querySelector('[data-personalization-file]');
      this.fileName = this.querySelector('[data-personalization-file-name]');
      this.preview = this.querySelector('[data-personalization-preview]');
      this.clearFile = this.querySelector('[data-personalization-clear-file]');
      this.flag = this.querySelector('[data-personalization-flag]');
      this.count = this.querySelector('[data-personalization-count]');
      this.error = this.querySelector('[data-personalization-error]');
      this.alwaysOpen = this.hasAttribute('data-always-open');
      this.required = this.hasAttribute('data-required');
      this.pendingSync = false;
      this.defaultFileText = this.fileName ? this.fileName.textContent : '';
      this.onToggle = this.onToggle.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onFileChange = this.onFileChange.bind(this);
      this.onClearFile = this.onClearFile.bind(this);
      this.onAdded = this.onAdded.bind(this);
      this.onError = this.onError.bind(this);
    }

    connectedCallback() {
      if (!this.panel) return;
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
      // One listener for every line; 'input' bubbles.
      this.addEventListener('input', this.onInput);
      if (this.file) this.file.addEventListener('change', this.onFileChange);
      if (this.clearFile) this.clearFile.addEventListener('click', this.onClearFile);
      this.form = document.getElementById(this.dataset.formId);
      if (this.form) {
        this.form.addEventListener('ajaxProduct:added', this.onAdded);
        this.form.addEventListener('ajaxProduct:error', this.onError);
      }
      this.onInput();
      this.syncFormState(this.isOpen);
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      this.removeEventListener('input', this.onInput);
      if (this.file) this.file.removeEventListener('change', this.onFileChange);
      if (this.clearFile) this.clearFile.removeEventListener('click', this.onClearFile);
      if (this.form) {
        this.form.removeEventListener('ajaxProduct:added', this.onAdded);
        this.form.removeEventListener('ajaxProduct:error', this.onError);
      }
    }

    // Every submittable control in the panel. Disabled controls are left out
    // of the form entirely, which is how the panel stays inert when closed.
    get fields() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-personalization-field]'));
    }

    get isOpen() {
      if (this.alwaysOpen) return true;
      return !!this.toggle && this.toggle.getAttribute('aria-expanded') === 'true';
    }

    // Every non-empty engraved line, in order.
    get textLines() {
      return this.inputs.map(function (i) { return i.value.trim(); }).filter(Boolean);
    }

    get text() {
      return this.textLines.join(' ');
    }

    get selectedFile() {
      return this.file && this.file.files && this.file.files[0] ? this.file.files[0] : null;
    }

    get hasContent() {
      return hasText(this.text) || !!this.selectedFile;
    }

    get isActive() {
      return this.isOpen && this.hasContent;
    }

    get feeVariantId() {
      return FEE_ENABLED && this.dataset.feeVariantId ? Number(this.dataset.feeVariantId) : null;
    }

    // Accelerated checkout (Shop Pay, Apple Pay, ...) skips /cart/add, so the
    // fee could never be attached. Hide those buttons while a paid
    // personalization is open; the class is styled in customization.css.
    syncFormState(open) {
      if (!this.form) return;
      this.form.classList.toggle('has-paid-personalization', !!(open && this.feeVariantId));
    }

    setOpen(open) {
      if (this.alwaysOpen) open = true;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.panel.hidden = !open;
      // A disabled field is left out of the form submission entirely.
      this.fields.forEach(function (field) { field.disabled = !open; });
      this.syncFormState(open);
      if (open) {
        if (this.input && !this.alwaysOpen) this.input.focus();
      } else {
        this.clearValues();
      }
    }

    clearValues() {
      this.inputs.forEach(function (input) { input.value = ''; });
      this.resetFile();
      this.onInput();
      this.hideError();
    }

    onToggle() {
      this.setOpen(!this.isOpen);
    }

    onInput() {
      this.inputs.forEach(function (input) {
        var holder = input.parentNode;
        var count = holder && holder.querySelector('[data-personalization-count]');
        if (count) count.textContent = String(input.value.length);
      });
    }

    onFileChange() {
      var file = this.selectedFile;
      this.hideError();
      if (!file) {
        this.resetFile();
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.resetFile();
        this.showError(this.dataset.fileTooLargeMessage || 'That file is too large.');
        return;
      }
      if (this.fileName) this.fileName.textContent = file.name;
      if (this.clearFile) this.clearFile.hidden = false;
      if (this.preview) {
        if (/^image\//.test(file.type) && window.URL && URL.createObjectURL) {
          this.revokePreview();
          this.previewUrl = URL.createObjectURL(file);
          this.preview.src = this.previewUrl;
          this.preview.hidden = false;
        } else {
          this.preview.hidden = true;
        }
      }
    }

    onClearFile() {
      this.resetFile();
      this.hideError();
    }

    revokePreview() {
      if (this.previewUrl && URL.revokeObjectURL) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    resetFile() {
      if (this.file) this.file.value = '';
      if (this.fileName) this.fileName.textContent = this.defaultFileText;
      if (this.clearFile) this.clearFile.hidden = true;
      if (this.preview) {
        this.preview.hidden = true;
        this.preview.removeAttribute('src');
      }
      this.revokePreview();
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    // Fields are submitted only when enabled; prepareSubmit narrows that per
    // submit, so put every field back to "enabled iff open" afterwards.
    restoreFields() {
      var open = this.isOpen;
      this.fields.forEach(function (field) { field.disabled = !open; });
    }

    onError() {
      this.pendingSync = false;
      this.restoreFields();
    }

    onAdded() {
      // Item is in the cart: clear the fields so a second add starts fresh.
      var needsSync = this.pendingSync;
      this.pendingSync = false;
      this.restoreFields();
      if (this.alwaysOpen) {
        this.clearValues();
      } else {
        this.setOpen(false);
      }
      if (needsSync) syncCartFees();
    }

    // Called right before the form submits. Returns one of:
    //   'block'     - validation failed; stop the submit entirely
    //   'theme'     - let the theme's own handler post the form
    //   'intercept' - post item + fee together as JSON (text-only personalization)
    prepareSubmit() {
      this.hideError();
      this.restoreFields();
      if (!this.isOpen) return 'theme';

      if (!this.hasContent) {
        if (this.required) {
          this.showError(this.dataset.requiredMessage || 'Please add your personalization first.');
          if (this.input) this.input.focus();
          return 'block';
        }
        // Open but empty: don't send empty properties, and no fee.
        this.fields.forEach(function (field) { field.disabled = true; });
        return 'theme';
      }

      // Trim each line, and leave empty ones out of the submission so a blank
      // second line never becomes an empty property on the order.
      this.inputs.forEach(function (input) {
        input.value = input.value.trim();
        input.disabled = !hasText(input.value);
      });

      if (!this.feeVariantId) return 'theme';

      if (this.selectedFile) {
        // Files need a multipart post, which only the theme's handler does.
        // Add the fee once the item is in the cart.
        this.pendingSync = true;
        return 'theme';
      }

      return 'intercept';
    }
  }

  if (!customElements.get('product-customization')) {
    customElements.define('product-customization', ProductCustomization);
  }

  /* ------------------------------------------------------------------
     Bead color picker
     Shown only while the chosen chain matches its rule (e.g. "Chain"
     contains "Beaded"). Radios are disabled while hidden so nothing is
     submitted or validated.
  ------------------------------------------------------------------ */
  class ProductBeadPicker extends HTMLElement {
    constructor() {
      super();
      this.onSectionChange = this.onSectionChange.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInvalid = this.onInvalid.bind(this);
    }

    connectedCallback() {
      this.group = this.closest('[data-bead-group]') || this;
      this.section = this.closest('.product-section') || this.closest('[data-product-blocks]') || document;
      this.error = this.querySelector('[data-bead-error]');
      this.selectedLabel = this.querySelector('[data-bead-selected]');
      this.section.addEventListener('change', this.onSectionChange);
      this.addEventListener('change', this.onChange);
      this.addEventListener('invalid', this.onInvalid, true);
      this.update();
    }

    disconnectedCallback() {
      if (this.section) this.section.removeEventListener('change', this.onSectionChange);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('invalid', this.onInvalid, true);
    }

    get inputs() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-bead-input]'));
    }

    get selected() {
      return this.querySelector('[data-bead-input]:checked');
    }

    matchesRule() {
      var handle = this.dataset.optionHandle;
      var needle = (this.dataset.optionContains || '').toLowerCase();
      if (!handle || !needle) return true;
      var input = this.section.querySelector('.variant-input-wrap[data-handle="' + handle + '"] input:checked');
      var value = input ? input.value : '';
      if (!input) {
        var select = this.section.querySelector('select[data-handle="' + handle + '"], select[name="' + handle + '"]');
        value = select ? select.value : '';
      }
      return value.toLowerCase().indexOf(needle) !== -1;
    }

    update() {
      var visible = this.matchesRule();
      this.group.toggleAttribute('hidden', !visible);
      this.inputs.forEach(function (input) { input.disabled = !visible; });
      if (!visible) this.hideError();
      this.dataset.metal = this.metal();
      this.renderSelected();
      this.applyMetalConstraint(visible);
    }

    // A swatch marked "Gold only" / "Silver only" hides the metal option row
    // and applies that metal. Rows we hide are tagged so the variant-based
    // hiding (syncSingleOptions) leaves them alone.
    applyMetalConstraint(visible) {
      var wrap = this.section.querySelector('.variant-input-wrap[data-handle="color"], .variant-input-wrap[data-handle="metal"]');
      if (!wrap) return;
      var row = wrap.closest('.variant-wrapper') || wrap;
      var picked = visible ? this.selected : null;
      var metal = picked && picked.dataset.metals && picked.dataset.metals !== 'both' ? picked.dataset.metals : null;
      if (metal) {
        var target = Array.prototype.filter.call(wrap.querySelectorAll('input[data-variant-input]'), function (input) {
          return input.value.toLowerCase().indexOf(metal) !== -1;
        })[0];
        row.setAttribute('hidden', '');
        row.dataset.hiddenByBead = '1';
        if (target && !target.checked) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (row.dataset.hiddenByBead) {
        row.removeAttribute('hidden');
        delete row.dataset.hiddenByBead;
      }
    }

    metal() {
      var input = this.section.querySelector('.variant-input-wrap[data-handle="color"] input:checked, .variant-input-wrap[data-handle="metal"] input:checked');
      var value = (input ? input.value : '').toLowerCase();
      return value.indexOf('silver') !== -1 || value.indexOf('white') !== -1 ? 'silver' : 'gold';
    }

    renderSelected() {
      if (!this.selectedLabel) return;
      var picked = this.selected;
      this.selectedLabel.textContent = picked ? '— ' + picked.value : '';
    }

    onSectionChange(evt) {
      if (evt.target.matches('[data-variant-input]')) this.update();
    }

    onChange(evt) {
      if (!evt.target.matches('[data-bead-input]')) return;
      this.hideError();
      this.renderSelected();
      this.inputs.forEach(function (input) {
        var option = input.closest('[data-bead-option]');
        if (option) option.classList.toggle('is-selected', input.checked);
      });
      this.applyMetalConstraint(this.matchesRule());
    }

    onInvalid(evt) {
      if (!evt.target.matches('[data-bead-input]')) return;
      this.showError(this.dataset.requiredMessage || 'Please choose a bead color.');
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }
  }

  if (!customElements.get('product-bead-picker')) {
    customElements.define('product-bead-picker', ProductBeadPicker);
  }

  /* ------------------------------------------------------------------
     Design picker ("What do you want on it?")
     One radio per design option; only the selected option's fields are
     enabled, so only they are submitted. Validation is the browser's own
     `required` handling, with a styled message on top of it.
  ------------------------------------------------------------------ */
  class ProductDesignPicker extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onInvalid = this.onInvalid.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onAdded = this.onAdded.bind(this);
      this.onVariantInput = this.onVariantInput.bind(this);
    }

    connectedCallback() {
      this.options = Array.prototype.slice.call(this.querySelectorAll('[data-design-option]'));
      this.error = this.querySelector('[data-design-error]');
      this.form = document.getElementById(this.dataset.formId);
      this.section = this.closest('.product-section') || this.closest('[data-product-blocks]') || document;
      this.preview = this.querySelector('[data-design-preview]');
      if (this.preview) {
        this.previewPendant = this.preview.querySelector('[data-preview-pendant]');
        this.previewDisc = this.preview.querySelector('[data-preview-disc]');
        this.previewHeart = this.preview.querySelector('[data-preview-heart]');
        this.previewTemple = this.preview.querySelector('[data-preview-temple]');
        this.previewBeads = this.preview.querySelector('[data-preview-beads]');
        this.previewToggle = this.preview.querySelector('[data-preview-toggle]');
        this.previewMap = this.preview.querySelector('[data-preview-map]');
        this.previewMapUtah = this.preview.querySelector('[data-preview-map-utah]');
        this.previewCaption = this.preview.querySelector('[data-preview-caption]');
        this.previewText = this.preview.querySelector('[data-preview-text]');
      }

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      // `invalid` doesn't bubble; listen in the capture phase.
      this.addEventListener('invalid', this.onInvalid, true);
      if (this.form) this.form.addEventListener('ajaxProduct:added', this.onAdded);
      this.section.addEventListener('change', this.onVariantInput);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('invalid', this.onInvalid, true);
      if (this.form) this.form.removeEventListener('ajaxProduct:added', this.onAdded);
      if (this.section) this.section.removeEventListener('change', this.onVariantInput);
    }

    fieldsFor(option) {
      var id = option.dataset.blockId;
      return id ? this.querySelector('[data-fields-for="' + id + '"]') : null;
    }

    get selectedOption() {
      for (var i = 0; i < this.options.length; i++) {
        var radio = this.options[i].querySelector('[data-design-input]');
        if (radio && radio.checked) return this.options[i];
      }
      return null;
    }

    update() {
      var self = this;
      this.options.forEach(function (option) {
        var radio = option.querySelector('[data-design-input]');
        var fields = self.fieldsFor(option);
        var selected = !!(radio && radio.checked);
        option.classList.toggle('is-selected', selected);
        if (!fields) return;
        fields.hidden = !selected;
        fields.querySelectorAll('[data-design-field]').forEach(function (field) {
          field.disabled = !selected;
        });
      });
      this.renderPreview();
    }

    onChange(evt) {
      if (!evt.target.matches('[data-design-input]')) return;
      this.update();
      this.hideError();
      var fields = this.fieldsFor(evt.target.closest('[data-design-option]'));
      var first = fields && fields.querySelector('[data-design-field]');
      if (first) first.focus({ preventScroll: true });
    }

    onInput(evt) {
      this.hideError();
      if (evt.target.matches('[data-design-field]')) this.renderPreview();
    }

    onVariantInput(evt) {
      if (evt.target.matches('[data-variant-input], [data-bead-input]')) this.renderPreview();
    }

    onInvalid(evt) {
      var el = evt.target;
      var message;
      if (el.matches('[data-design-input]')) {
        message = this.dataset.requiredMessage || 'Please choose a design.';
      } else if (el.matches('[data-design-field]')) {
        message = 'Please fill in "' + (el.dataset.label || 'this field') + '".';
      } else {
        return;
      }
      this.showError(message);
    }

    onAdded() {
      this.querySelectorAll('[data-design-field]').forEach(function (field) { field.value = ''; });
      this.hideError();
      this.renderPreview();
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    /* ---- live preview ------------------------------------------------ */

    variantValue(handle) {
      var input = this.section.querySelector('.variant-input-wrap[data-handle="' + handle + '"] input:checked');
      if (input) return input.value;
      var select = this.section.querySelector('.variant-wrapper select[data-handle="' + handle + '"], select[name="' + handle + '"]');
      return select ? select.value : '';
    }

    metal() {
      var value = (this.variantValue('color') || this.variantValue('colour') || this.variantValue('metal') || '').toLowerCase();
      if (value.indexOf('silver') !== -1 || value.indexOf('white') !== -1) return 'silver';
      return 'gold';
    }

    sizeScale() {
      var wrap = this.section.querySelector('.variant-input-wrap[data-handle="size"]');
      if (!wrap) return 1;
      var values = Array.prototype.map.call(wrap.querySelectorAll('.variant-input'), function (el) {
        return parseFloat(el.dataset.value);
      }).filter(function (n) { return !isNaN(n) && n > 0; });
      var current = parseFloat(this.variantValue('size'));
      if (!values.length || isNaN(current)) return 1;
      var max = Math.max.apply(null, values);
      return Math.max(0.6, current / max);
    }

    previewLines() {
      var option = this.selectedOption;
      if (!option) return { mode: 'text', lines: [], placeholder: 'Your engraving' };
      var mode = option.dataset.preview || 'text';
      var fields = this.fieldsFor(option);
      var values = fields
        ? Array.prototype.map.call(fields.querySelectorAll('[data-design-field]'), function (f) { return f.value.trim(); })
        : [];
      if (mode === 'lines') {
        return { mode: mode, lines: values.filter(Boolean), placeholder: 'Place & date' };
      }
      if (mode === 'temple') {
        // With two fields (temple name + date) the date is the second one.
        var templeDate = values.length > 1 ? values[1] : values[0];
        return { mode: mode, lines: templeDate ? [templeDate] : [], placeholder: '' };
      }
      if (mode === 'map' || mode === 'map-utah') {
        // Place is drawn (as the example country/state); the date stamps underneath.
        var date = values.length > 1 ? values[1] : '';
        return { mode: mode, lines: date ? [date] : [], placeholder: '' };
      }
      if (values[0]) {
        // "Alma 7:11-12" stamps as ALMA / 7:11-12: book on one line, reference under it.
        var match = values[0].match(/^(.*\S)\s+(\d+[:.\-\u2013]\S*)$/);
        if (match) return { mode: mode, lines: [match[1], match[2]], placeholder: '' };
        return { mode: mode, lines: [values[0]], placeholder: '' };
      }
      return { mode: mode, lines: [], placeholder: 'Your engraving' };
    }

    renderPreview() {
      if (!this.preview || !this.previewText) return;
      var metal = this.metal();
      var scale = this.sizeScale();
      var data = this.previewLines();

      this.preview.dataset.metal = metal;
      if (this.previewDisc) {
        this.previewDisc.setAttribute('transform', 'scale(' + scale.toFixed(3) + ')');
        this.previewDisc.setAttribute('fill', 'url(#pendant-' + metal + '-' + (this.preview.dataset.uid || '') + ')');
      }

      var radius = 76 * scale;
      var heart = data.mode === 'heart';
      if (this.previewHeart) {
        this.previewHeart.toggleAttribute('hidden', !heart);
        if (heart) {
          var hs = (radius * 1.55) / 100; // heart path is 100 x 88; sits centered in the disc
          this.previewHeart.setAttribute('transform',
            'translate(' + (-50 * hs).toFixed(2) + ' ' + (-44 * hs + 3).toFixed(2) + ') scale(' + hs.toFixed(3) + ')');
        }
      }

      // Toggle clasp: show the bar through the ring when a toggle-style swatch is chosen
      if (this.previewToggle) {
        var toggle = !!this.section.querySelector('[data-bead-input][data-icon="toggle"]:checked:not(:disabled)');
        this.previewToggle.toggleAttribute('hidden', !toggle);
      }

      // Beaded chain: beads along the chain lines, colored by the bead choice
      if (this.previewBeads) {
        var beaded = /bead/i.test(this.variantValue('chain') || '');
        this.previewBeads.toggleAttribute('hidden', !beaded);
        if (beaded) {
          var palette = this.beadPalette();
          var key = palette.join(',');
          if (key !== this.beadPaletteKey) {
            this.beadPaletteKey = key;
            this.buildBeads(palette);
          }
        }
      }

      var temple = data.mode === 'temple';
      if (this.previewTemple) {
        this.previewTemple.toggleAttribute('hidden', !temple);
        if (temple) {
          var ts = (radius * 1.05) / 100; // temple art is 100 wide
          this.previewTemple.setAttribute('transform',
            'translate(' + (-50 * ts).toFixed(2) + ' ' + (-radius * 0.72).toFixed(2) + ') scale(' + ts.toFixed(3) + ')');
        }
      }

      // Example maps: art lives in a 100 x 100 box; scale/top tuned per map
      var maps = {
        'map': { el: this.previewMap, scale: 1.6, top: 0.88, caption: 'mapCaption' },
        'map-utah': { el: this.previewMapUtah, scale: 1.15, top: 0.8, caption: 'mapUtahCaption' }
      };
      var map = !!maps[data.mode];
      var self = this;
      Object.keys(maps).forEach(function (key) {
        var cfg = maps[key];
        if (!cfg.el) return;
        var on = data.mode === key;
        cfg.el.toggleAttribute('hidden', !on);
        if (on) {
          var ms = (radius * cfg.scale) / 100;
          cfg.el.setAttribute('transform',
            'translate(' + (-50 * ms).toFixed(2) + ' ' + (-radius * cfg.top).toFixed(2) + ') scale(' + ms.toFixed(3) + ')');
        }
      });
      if (this.previewCaption) {
        var captionKey = map ? maps[data.mode].caption : null;
        this.previewCaption.textContent = (captionKey && this.previewCaption.dataset[captionKey]) || this.previewCaption.dataset.defaultCaption;
      }

      if (data.mode === 'none') {
        this.previewText.textContent = '';
        return;
      }

      var lines = data.lines.map(function (line) { return line.toUpperCase(); });
      if (!lines.length && !data.placeholder) {
        this.previewText.textContent = '';
        return;
      }
      var text = lines.length ? lines : [data.placeholder];
      this.previewText.classList.toggle('is-placeholder', !lines.length);

      if (temple || map) {
        // Date sits under the artwork, like the stamped piece.
        this.fitText(text.join(' '), text, radius * 1.1, 1, map ? radius * 0.7 : radius * 0.62, 12);
        return;
      }

      var maxWidth = heart ? radius * 0.9 : radius * 1.45;
      var preset = data.mode === 'lines' || lines.length === 2 ? text : null;
      this.fitText(text.join(' '), preset, maxWidth, preset ? preset.length : 3, heart ? -radius * 0.03 : 0, heart ? 14 : 20);
    }

    beadPalette() {
      var picked = this.section.querySelector('[data-bead-input][data-colors]:checked');
      var base = picked && picked.dataset.colors
        ? picked.dataset.colors.split(',').map(function (c) { return c.trim(); }).filter(Boolean)
        : (paletteFor(this.variantValue('chain')) || ['#f3cbd6', '#cfe2f3', '#f8f3ea', '#e6d7ee']);
      if (base.length >= 3) return base;
      // Single/dual colors: add a lighter and a darker tint so the strand has depth
      var out = base.slice();
      out.push(tint(base[0], 0.35));
      out.push(tint(base[0], -0.18));
      return out;
    }

    buildBeads(colors) {
      while (this.previewBeads.firstChild) this.previewBeads.removeChild(this.previewBeads.firstChild);
      var lines = [[40, 0, 114, 60], [200, 0, 126, 60]];
      var ns = 'http://www.w3.org/2000/svg';
      var self = this;
      lines.forEach(function (l, li) {
        var steps = 11;
        for (var i = 0; i <= steps; i++) {
          var t = i / steps;
          var bead = document.createElementNS(ns, 'circle');
          bead.setAttribute('cx', (l[0] + (l[2] - l[0]) * t).toFixed(1));
          bead.setAttribute('cy', (l[1] + (l[3] - l[1]) * t).toFixed(1));
          bead.setAttribute('r', i % 3 === 0 ? 3.2 : 2.6);
          bead.setAttribute('fill', colors[(i + li) % colors.length]);
          self.previewBeads.appendChild(bead);
        }
      });
    }

    // Wrap words into at most maxLines lines and shrink the font until it
    // fits inside maxWidth. Measured with the real font, so it stays honest.
    fitText(joined, forcedLines, maxWidth, maxLines, yOffset, startSize) {
      var textEl = this.previewText;
      var size = startSize || 20;
      var lines;
      for (; size >= 7; size -= 1) {
        lines = forcedLines || this.wrapWords(joined, Math.max(4, Math.floor(maxWidth / (size * 0.66))));
        if (lines.length > maxLines) continue;
        this.setLines(textEl, lines, size, yOffset);
        var widest = 0;
        textEl.querySelectorAll('tspan').forEach(function (t) {
          try { widest = Math.max(widest, t.getComputedTextLength()); } catch (e) { widest = 0; }
        });
        if (widest <= maxWidth) return;
      }
      this.setLines(textEl, lines || [joined], 7, yOffset);
    }

    wrapWords(text, maxChars) {
      var words = text.split(/\s+/).filter(Boolean);
      var lines = [];
      var current = '';
      words.forEach(function (word) {
        var candidate = current ? current + ' ' + word : word;
        if (candidate.length > maxChars && current) {
          lines.push(current);
          current = word;
        } else {
          current = candidate;
        }
      });
      if (current) lines.push(current);
      return lines;
    }

    setLines(textEl, lines, size, yOffset) {
      var lineHeight = size * 1.15;
      var total = lineHeight * (lines.length - 1);
      textEl.setAttribute('font-size', size);
      textEl.textContent = '';
      lines.forEach(function (line, i) {
        var tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspan.setAttribute('x', 0);
        tspan.setAttribute('y', (yOffset - total / 2 + i * lineHeight + size * 0.35).toFixed(2));
        tspan.textContent = line;
        textEl.appendChild(tspan);
      });
    }
  }

  if (!customElements.get('product-design-picker')) {
    customElements.define('product-design-picker', ProductDesignPicker);
  }

  /* ------------------------------------------------------------------
     Price hints on variant buttons ("+$25.00" next to Beaded, etc.)
     Enabled per product page via the variant picker block setting.
  ------------------------------------------------------------------ */
  // Bead palettes by chain/bead name, used for the preview and the option dots
  var BEAD_PALETTES = [
    [/pastel/i, ['#f3cbd6', '#cfe2f3', '#f8f3ea', '#e6d7ee']],
    [/sea ?glass|aventurine|jade|green/i, ['#a9d9c5', '#8cc7ae', '#c9e7d8', '#79b89b']],
    [/turquoise|teal|aqua/i, ['#4fb5bd', '#86d3d6', '#2e8f98']],
    [/purple|lavender|amethyst|lilac/i, ['#b9a2dc', '#d6c6ec', '#9d84c9']],
    [/pearl|white|ivory/i, ['#f4efe6', '#fdfbf7', '#e6dccb']],
    [/pink|rose|blush/i, ['#f2c1cf', '#f9dde6', '#e8a9bc']]
  ];

  function paletteFor(name) {
    for (var i = 0; i < BEAD_PALETTES.length; i++) {
      if (BEAD_PALETTES[i][0].test(name || '')) return BEAD_PALETTES[i][1];
    }
    return null;
  }

  // Lighten (amount > 0) or darken (amount < 0) a hex color
  function tint(hex, amount) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return hex;
    var n = parseInt(m[1], 16);
    var parts = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(function (c) {
      var v = amount > 0 ? c + (255 - c) * amount : c * (1 + amount);
      return Math.max(0, Math.min(255, Math.round(v)));
    });
    return '#' + parts.map(function (c) { return ('0' + c.toString(16)).slice(-2); }).join('');
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function readVariants(section) {
    var jsonEl = section.querySelector('[data-variant-json]');
    if (!jsonEl) return null;
    try {
      var variants = JSON.parse(jsonEl.textContent);
      return Array.isArray(variants) && variants.length ? variants : null;
    } catch (e) { return null; }
  }

  // Hide an option row (e.g. Gold / Silver) while the other selections leave
  // only one value available, and select that value.
  function syncSingleOptions(section) {
    var block = section.querySelector('[data-hide-single]');
    if (!block) return;
    var variants = readVariants(section);
    if (!variants) return;
    var wraps = Array.prototype.slice.call(block.querySelectorAll('.variant-input-wrap'));
    wraps.forEach(function (wrap) {
      var idx = wrap.dataset.index;
      var others = {};
      wraps.forEach(function (w) {
        if (w === wrap) return;
        var checked = w.querySelector('input:checked');
        if (checked) others[w.dataset.index] = checked.value;
      });
      var available = [];
      variants.forEach(function (v) {
        if (!v.available) return;
        for (var k in others) { if (v[k] !== others[k]) return; }
        if (available.indexOf(v[idx]) === -1) available.push(v[idx]);
      });
      var row = wrap.closest('.variant-wrapper') || wrap;
      if (row.dataset.hiddenByBead) return; // a bead swatch owns this row right now
      var single = available.length === 1;
      row.toggleAttribute('hidden', single);
      if (single) {
        var input = Array.prototype.filter.call(wrap.querySelectorAll('input[data-variant-input]'), function (i) {
          return i.value === available[0];
        })[0];
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  document.addEventListener('change', function (evt) {
    if (!evt.target.matches || !evt.target.matches('[data-variant-input]')) return;
    var section = evt.target.closest('.product-section');
    if (section) syncSingleOptions(section);
  });

  function initPriceHints(root) {
    (root || document).querySelectorAll('[data-hide-single]').forEach(function (block) {
      var section = block.closest('.product-section');
      if (section && !block.dataset.hideSingleDone) {
        block.dataset.hideSingleDone = '1';
        syncSingleOptions(section);
      }
    });

    (root || document).querySelectorAll('[data-price-hints]').forEach(function (block) {
      if (block.dataset.priceHintsDone) return;
      var section = block.closest('.product-section') || document;
      var jsonEl = section.querySelector('[data-variant-json]');
      if (!jsonEl) return;
      var variants;
      try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
      if (!Array.isArray(variants) || !variants.length) return;

      var base = Math.min.apply(null, variants.map(function (v) { return v.price; }));

      block.querySelectorAll('.variant-input').forEach(function (input) {
        var optionKey = input.dataset.index; // "option1", "option2", ...
        var value = input.dataset.value;
        var wrapEl = input.closest('.variant-input-wrap');
        var labelEl = input.querySelector('.variant__button-label');
        if (labelEl && wrapEl && wrapEl.dataset.handle === 'chain' && !labelEl.querySelector('.variant__bead-dot')) {
          var palette = paletteFor(value);
          if (palette) {
            var dot = document.createElement('span');
            dot.className = 'variant__bead-dot';
            dot.style.background = 'linear-gradient(135deg, ' + palette[0] + ' 0 34%, ' + palette[1] + ' 34% 67%, ' + palette[palette.length - 1] + ' 67%)';
            labelEl.insertBefore(dot, labelEl.firstChild);
          }
        }
        var prices = variants
          .filter(function (v) { return v[optionKey] === value; })
          .map(function (v) { return v.price; });
        if (!prices.length) return;
        var delta = Math.min.apply(null, prices) - base;
        if (delta <= 0) return;
        var label = input.querySelector('.variant__button-label');
        if (!label || label.querySelector('.variant__price-hint')) return;
        var hint = document.createElement('span');
        hint.className = 'variant__price-hint';
        hint.textContent = '+' + formatMoney(delta);
        label.appendChild(hint);
      });

      block.dataset.priceHintsDone = '1';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initPriceHints(); });
  } else {
    initPriceHints();
  }
  document.addEventListener('shopify:section:load', function (evt) { initPriceHints(evt.target); });
  // Quick-shop modals load product blocks after the page; catch those too.
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) { initPriceHints(); return; }
    }
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });

  /* ------------------------------------------------------------------
     Add to cart with the fee line
  ------------------------------------------------------------------ */
  function componentForForm(form) {
    if (!form || !form.id) return null;
    var all = document.querySelectorAll('product-customization');
    for (var i = 0; i < all.length; i++) {
      if (all[i].dataset.formId === form.id) return all[i];
    }
    return null;
  }

  function formToItem(form) {
    var data = new FormData(form);
    var item = {
      id: Number(data.get('id')),
      quantity: parseInt(data.get('quantity'), 10) || 1,
      properties: {}
    };
    data.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string' && hasText(value)) item.properties[match[1]] = value;
    });
    if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
    return item;
  }

  function feeItem(feeVariantId, forVariantId, quantity, productTitle) {
    var properties = {};
    properties[FOR_PROPERTY] = productTitle || '';
    properties[LINK_PROPERTY] = String(forVariantId);
    return { id: feeVariantId, quantity: quantity, properties: properties };
  }

  function showFormError(form, message) {
    var existing = form.querySelector('.errors');
    if (existing) existing.remove();
    var div = document.createElement('div');
    div.classList.add('errors', 'text-center');
    div.textContent = message;
    form.append(div);
  }

  var adding = false;

  function addWithFee(form, component) {
    var button = form.querySelector('[data-add-to-cart]') || form.querySelector('[type="submit"]');
    var item = formToItem(form);
    var fee = feeItem(component.feeVariantId, item.id, item.quantity, component.dataset.productTitle);

    adding = true;
    if (button) {
      button.classList.add('btn--loading');
      button.disabled = true;
    }

    var unlock = function () {
      adding = false;
      if (button) {
        button.classList.remove('btn--loading');
        button.disabled = false;
      }
    };

    var finish = function (data) {
      unlock();
      var existing = form.querySelector('.errors');
      if (existing) existing.remove();

      if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
        window.location = routes().cartPage;
        return;
      }

      var added = data && data.items ? data.items[0] : data;
      form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: added, addToCartBtn: button },
        bubbles: true
      }));

      if (document.body.classList.contains('template-cart')) {
        window.scrollTo(0, 0);
        location.reload();
      }
    };

    var fail = function (data) {
      unlock();
      var message = (data && (data.description || data.message)) || 'Something went wrong. Please try again.';
      if (typeof message === 'object') message = data.message;
      showFormError(form, message);
      form.dispatchEvent(new CustomEvent('ajaxProduct:error', {
        detail: { errorMessage: message },
        bubbles: true
      }));
    };

    postJSON(routes().cartAdd, { items: [item, fee] })
      .then(function (data) {
        if (data && data.status && data.status !== 200) {
          // The fee product may be unavailable; never block the sale over it.
          console.warn('[personalization] fee line rejected, adding item without fee:', data);
          return postJSON(routes().cartAdd, { items: [item] }).then(function (retry) {
            if (retry && retry.status && retry.status !== 200) return fail(retry);
            finish(retry);
          });
        }
        finish(data);
      })
      .catch(function (err) {
        console.error('[personalization] add failed', err);
        fail(null);
      });
  }

  // Capture phase so this runs before the theme's own submit handler.
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

    var component = componentForForm(form);
    if (!component) return;

    if (adding) {
      // A paired add is already in flight; swallow the repeat submit.
      evt.preventDefault();
      evt.stopImmediatePropagation();
      return;
    }

    var action = component.prepareSubmit();
    if (action === 'block') {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      return;
    }
    if (action !== 'intercept') return; // theme handles the add; the properties ride along

    evt.preventDefault();
    evt.stopImmediatePropagation();
    addWithFee(form, component);
  }, true);

  /* ------------------------------------------------------------------
     Keep fee lines in sync with personalized items
  ------------------------------------------------------------------ */
  var reconciling = false;

  function planReconcile(cart) {
    var needed = {};
    var titles = {};
    var ops = [];

    // Fee lines from a previously selected fee product no longer belong to
    // anything; remove them so the customer isn't charged twice.
    (cart.items || []).forEach(function (line) {
      var props = line.properties || {};
      if (props[LINK_PROPERTY] && line.variant_id !== FEE_ID) {
        ops.push({ url: routes().cartChange, body: { id: line.key, quantity: 0 } });
      }
    });

    if (!FEE_ENABLED) return ops;

    (cart.items || []).forEach(function (line) {
      if (line.variant_id === FEE_ID) return;
      var props = line.properties || {};
      if (!props[MARK_PROPERTY] && !hasText(props[PROPERTY])) return;
      needed[line.variant_id] = (needed[line.variant_id] || 0) + line.quantity;
      titles[line.variant_id] = line.product_title;
    });

    (cart.items || []).forEach(function (line) {
      if (line.variant_id !== FEE_ID) return;
      var props = line.properties || {};
      var forVariant = props[LINK_PROPERTY];
      if (!forVariant) return; // not one of ours
      var want = needed[forVariant] || 0;
      if (want !== line.quantity) {
        ops.push({ url: routes().cartChange, body: { id: line.key, quantity: want } });
      }
      delete needed[forVariant];
    });

    Object.keys(needed).forEach(function (variantId) {
      ops.push({
        url: routes().cartAdd,
        body: { items: [feeItem(FEE_ID, variantId, needed[variantId], titles[variantId])] }
      });
    });

    return ops;
  }

  function reconcile(cart) {
    if (!FEE_ID || reconciling || !cart || !cart.items) return Promise.resolve(false);
    var ops = planReconcile(cart);
    if (!ops.length) return Promise.resolve(false);

    reconciling = true;
    // Cart mutations must run one at a time.
    return ops.reduce(function (chain, op) {
      return chain.then(function () { return postJSON(op.url, op.body); });
    }, Promise.resolve())
      .then(function () { reconciling = false; return true; })
      .catch(function (err) {
        reconciling = false;
        console.error('[personalization] cart sync failed', err);
        return false;
      });
  }

  function rebuildCart() {
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  function syncCartFees() {
    return getCart().then(reconcile).then(function (changed) { if (changed) rebuildCart(); })
      .catch(function () {});
  }

  document.addEventListener('cart:updated', function (evt) {
    var cart = evt.detail && evt.detail.cart;
    reconcile(cart).then(function (changed) { if (changed) rebuildCart(); });
  });

  // Shopify sets a `cart` cookie once the cart has anything in it.
  if (FEE_ID && /(^|;\s*)cart=/.test(document.cookie)) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncCartFees);
    } else {
      syncCartFees();
    }
  }
})();
